import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable, of } from 'rxjs';
import { StoredAuthTokens } from '@kikkz/b04/auth/util';
import { EMBEDDED_PARENT_ORIGIN } from '@kikkz/b04/auth/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';

const QUIZ_APP_PREFIX = 'w11-quiz-app';
const QUIZ_APP_ACTION_LOAD_DATA = `${QUIZ_APP_PREFIX}-load-data` as const;
const QUIZ_APP_ACTION_SAVE_DATA = `${QUIZ_APP_PREFIX}-save-data` as const;

const PARENT_RESPONSE_TIMEOUT_MS = 500;

@Injectable({
  providedIn: 'root',
})
export class ParentBridgeService {
  private readonly document = inject(DOCUMENT);
  private readonly embeddedParentOrigin = inject(EMBEDDED_PARENT_ORIGIN);
  private readonly loggingService = inject(LOGGING_SERVICE, { optional: true });

  get isEmbedded(): boolean {
    const currentWindow = this.document.defaultView;
    return !!currentWindow && currentWindow.parent !== currentWindow;
  }

  requestTokensFromParent$(): Observable<StoredAuthTokens | null> {
    const currentWindow = this.document.defaultView;

    if (!currentWindow) {
      this.loggingService?.warn('ParentBridge: Cannot access window object.');
      return of(null);
    }

    return new Observable<StoredAuthTokens | null>((subscriber) => {
      const channel = new MessageChannel();
      const timeoutId = setTimeout(() => {
        this.loggingService?.warn('ParentBridge: Token request timed out.');
        subscriber.next(null);
        subscriber.complete();
      }, PARENT_RESPONSE_TIMEOUT_MS);

      channel.port1.onmessage = (event: MessageEvent) => {
        clearTimeout(timeoutId);

        const response = event.data;
        if (response?.status === 'ok' && response.data) {
          try {
            const tokens = JSON.parse(response.data) as StoredAuthTokens;
            if (this.isValidStoredAuthTokens(tokens)) {
              this.loggingService?.info('ParentBridge: Received valid tokens from parent.');
              subscriber.next(tokens);
            } else {
              this.loggingService?.warn('ParentBridge: Parent returned invalid token structure.');
              subscriber.next(null);
            }
          } catch (e) {
            this.loggingService?.warn('ParentBridge: Failed to parse tokens from parent.', e);
            subscriber.next(null);
          }
        } else {
          this.loggingService?.info('ParentBridge: Parent has no stored tokens.');
          subscriber.next(null);
        }

        subscriber.complete();
      };

      this.loggingService?.info('ParentBridge: Requesting tokens from parent...');
      currentWindow.parent.postMessage({ type: QUIZ_APP_ACTION_LOAD_DATA }, this.embeddedParentOrigin, [channel.port2]);
    });
  }

  sendTokensToParent(tokens: StoredAuthTokens): void {
    const currentWindow = this.document.defaultView;

    if (!currentWindow) {
      return;
    }

    const channel = new MessageChannel();
    // Fire-and-forget
    channel.port1.onmessage = () => {
      this.loggingService?.info('ParentBridge: Tokens synced to parent.');
    };

    this.loggingService?.info('ParentBridge: Sending tokens to parent for backup...');
    currentWindow.parent.postMessage(
      { type: QUIZ_APP_ACTION_SAVE_DATA, payload: JSON.stringify(tokens) },
      this.embeddedParentOrigin,
      [channel.port2],
    );
  }

  private isValidStoredAuthTokens(obj: unknown): obj is StoredAuthTokens {
    if (!obj || typeof obj !== 'object') return false;
    const t = obj as Record<string, unknown>;
    return (
      typeof t['access_token'] === 'string' &&
      typeof t['refresh_token'] === 'string' &&
      typeof t['id_token'] === 'string' &&
      typeof t['expires_in'] === 'number' &&
      typeof t['expiresAt'] === 'string'
    );
  }
}
